export const siteData = {
  brand: {
    name: "Operational Alpha",
    shortName: "OA",
  },

  nav: [
    { label: "Home", href: "#top" },
    { label: "Thesis", href: "#thesis" },
    { label: "AI Edge", href: "#product-engine" },
    { label: "Team", href: "#team" },
    { label: "Contact", href: "#contact" },
  ],

  hero: {
    eyebrow: "AI-Enabled Private Capital",
    headlines: [
      "We acquire businesses.",
      "We embed AI.",
      "We build platforms.",
    ],
    subtext:
      "Operational Alpha is a global private capital firm that acquires profitable SMEs and transforms them into tech-enabled platforms using a proprietary Agentic AI operating system.",
    stats: [
      { value: "Southeast Asia & Europe" },
      { value: "Control Acquisitions" },
      { value: "Agentic AI Operating System" },
      { value: "Operational Value Creation" },
    ],
  },

  thesis: {
    number: "01",
    label: "Investment Thesis",
    headline: "A generational transfer of business ownership is underway.",
    body: "Across Southeast Asia and Europe, millions of profitable SMEs face the same problem. Founders are aging out. No succession plan exists. No digital infrastructure is in place. These businesses generate strong cash flows but have nowhere to go. We step in as the next-generation operator.",
    statBlocks: [
      {
        region: "Southeast Asia",
        stat: "70%",
        description:
          "of SMEs have no succession plan. 76% are still in early stages of digitalization. Profitable businesses trapped in analog operations.",
      },
      {
        region: "Europe",
        stat: "200K+",
        description:
          "German business owners considering closure due to lack of successors. 47% of French owners over 60 are unprepared for transition.",
      },
    ],
    whatWeLookFor: [
      "Profitable businesses with strong recurring revenue",
      "Stable operations with loyal customer bases",
      "Founders ready for a transition or exit",
      "Significant manual processes ripe for automation",
      "Clear path to technology-driven value creation",
    ],
    ourApproach: [
      "We take control positions in businesses across corporate services, logistics, BPO, and healthcare. We deploy our proprietary AI operating system to transform operations. And we build these companies into tech-enabled platforms that attract premium buyers.",
      "Every acquisition follows the same playbook. Financial stabilization in 30 days. Operational mapping in 60. Full KPI visibility in 100. AI deployment from there.",
    ],
    geographies: ["Southeast Asia", "Indonesia", "Thailand", "Germany", "France"],
  },

  productEngine: {
    number: "02",
    label: "The Product Engine",
    headline: "AI that works inside the business. Not beside it.",
    body: "Most AI in private equity is a slide in the deck. Ours is the operating system. We build the infrastructure that allows autonomous AI agents to perform actual labor inside acquired businesses. Not chatbots. Not dashboards. Agents that execute multi-step workflows from end to end.",
    comparison: {
      left: {
        title: "Generative AI",
        subtitle: "Responds to prompts",
        points: [
          "Chatbots and copilots",
          "Requires human oversight",
          "Single-task completion",
          "Surface-level productivity gains",
        ],
      },
      right: {
        title: "Agentic AI",
        subtitle: "Executes workflows end-to-end",
        points: [
          "Autonomous multi-step agents",
          "Reasons, plans, and self-corrects",
          "End-to-end process execution",
          "Replaces entire workflow layers",
        ],
      },
    },
    callout: {
      title: "The Compounding Advantage",
      body: "Our orchestration layer deploys repeatable AI playbooks across every portfolio company simultaneously. The same platform that transforms Deal 1 deploys to Deal 5 with near-zero incremental engineering and scaling cost. This is not a consulting engagement. It is a reusable technology platform that gets stronger with every acquisition.",
    },
    capabilities: [
      {
        number: "01",
        title: "Financial Intelligence",
        sector: "Corporate Services",
        description:
          "Autonomous extraction of financial data. Inconsistency flagging. Tax strategy modeling. Draft proposals generated in minutes where teams previously spent days.",
        icon: "Calculator",
      },
      {
        number: "02",
        title: "Supply Chain Orchestration",
        sector: "Logistics",
        description:
          "Real-time carrier monitoring. Automated delay rerouting. ERP updates and customer notifications without human intervention. Penalty costs eliminated.",
        icon: "Container",
      },
      {
        number: "03",
        title: "Contract Analysis",
        sector: "Back-Office / Legal",
        description:
          "Legacy contract extraction and summarization. Obligation mapping. Reconciliation with accounting records. Orders of magnitude faster than manual review.",
        icon: "FileSearch",
      },
      {
        number: "04",
        title: "Growth Identification",
        sector: "Sales",
        description:
          "Market signal monitoring. CRM cross-referencing. Automatic surfacing of high-probability targets. Sales teams focus on closing, not prospecting.",
        icon: "Target",
      },
    ],
  },

  team: {
    number: "03",
    label: "Team",
    headline: "Operators. Not allocators.",
    body: "Our team combines institutional deal execution, product engineering at scale, and Fortune 500 commercial expertise. Three of four partners have built and scaled businesses in Southeast Asia for over a decade. We work inside portfolio companies. Not from board seats.",
    members: [
      {
        name: "Rick Marchese",
        role: "Chief Investment Officer",
        initials: "RM",
        lead: "Deal Sourcing + Structuring + LP Relations",
        linkedin: "https://www.linkedin.com/in/rick-marchese-56a1552/",
        bio: "25+ years of investment banking and private capital experience across Wall Street and Singapore. Career spans Merrill Lynch, Deutsche Bank, and DLJ. Operates Lares Loreno Private Capital in Singapore since 2012. MBA Chicago Booth. BS United States Naval Academy. Professor of Practice in Finance at ESSEC Business School Singapore.",
      },
      {
        name: "Marek Dawidowicz",
        role: "Partner, Digital Transformation",
        initials: "MD",
        lead: "Go-to-Market + Brand Building + Digital Adoption",
        linkedin: "https://www.linkedin.com/in/marekdawidowicz/",
        bio: "APAC Marketing Director at YouTube for 12+ years leading Subscriptions, Shorts, and Growth Marketing. Former Marketing Director at Red Bull, launching in South Korea and scaling across South Asia. Senior roles at Unilever, PepsiCo, and Kantar Consulting. Active angel investor in 25+ SME tech companies.",
      },
      {
        name: "Dipendra Jain",
        role: "Partner, Product & Technology",
        initials: "DJ",
        lead: "Product Engine + AI Architecture + Technology Stack",
        linkedin: "https://www.linkedin.com/in/dipendrajain/",
        bio: "4 companies founded with 3 successful acquisitions. Built the Skyscanner e-commerce marketplace to $4B GMV and 100M+ MAU. Technical background in AI/ML predating the current cycle, focused on small language models and on-device processing. MS AI/ML UT Austin. MBA IMD. Angel investor in 40+ companies.",
      },
      {
        name: "Nitin Gajria",
        role: "Advisor",
        initials: "NG",
        lead: "Revenue Engines + Partnerships + Market Expansion",
        linkedin: "https://www.linkedin.com/in/nitin-gajria/",
        bio: "Managing Director at Google leading APAC Publisher Partnerships across multiple geographies for 12 years. Former Google Country Director for Vietnam, Cambodia, and Laos. MD for Sub-Saharan Africa. 11 years at Procter & Gamble managing brands across ASEAN, Australia, and India. MBA IIM Calcutta.",
      },
    ],
    collectiveStats: [
      { value: "$4B+", label: "Marketplace GMV Built" },
      { value: "4+", label: "Successful Exits" },
      { value: "80+", label: "Angel Investments" },
      { value: "50+", label: "Years in SEA" },
      { value: "60+", label: "Years Combined Experience" },
    ],
  },

  contact: {
    number: "04",
    label: "Get in Touch",
    headline: "Currently deploying Fund I.",
    body: "We are actively acquiring businesses and speaking with aligned capital partners. If you are an LP, advisor, or business owner exploring a transition, we'd welcome a conversation.",
    cards: [
      {
        title: "Investors",
        description:
          "Institutional LPs, family offices, and high-net-worth individuals. Request the confidential information memorandum and fund model.",
        cta: "Request materials",
      },
      {
        title: "Business Owners",
        description:
          "If you run a profitable SME and are considering succession, a sale, or a partnership with an operator who invests in growth, let's talk.",
        cta: "Start a conversation",
      },
      {
        title: "Advisors & Brokers",
        description:
          "M&A advisors, corporate finance firms, and business brokers with deal flow in our target sectors and geographies.",
        cta: "Explore partnership",
      },
    ],
    directContacts: [
      {
        name: "Rick Marchese",
        area: "Investment & LP Relations",
        email: "rick@operationalalpha.ai",
      },
      {
        name: "Dipendra Jain",
        area: "Product & Technology",
        email: "dipendra@operationalalpha.ai",
      },
      {
        name: "Nitin Gajria",
        area: "Commercial & Partnerships",
        email: "nitin@operationalalpha.ai",
      },
      {
        name: "Marek Dawidowicz",
        area: "Digital Transformation",
        email: "marek@operationalalpha.ai",
      },
    ],
  },

  footer: {
    brand: "Operational Alpha",
    location: "Global",
    year: "2026",
  },
};
